import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
	ampEventWithEventProperty,
	ampSetUserProperty,
} from '../../../utils/amplitude'
import { purchasePropertyFetch } from '../../../utils/analyticsRequest'
import initialState from '../../initialStates/user'

export const sendConfirmationEmail = createAsyncThunk(
	'user/sendEmail',
	async body => {
		const data = await fetch(
			`${process.env.REACT_APP_SERVER_URL}/api/sendios/email`,
			{
				method: 'POST',
				headers: {
					auth: '135017',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(body),
			},
		)
		return data.status
	},
)

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		setEmail: (state, action) => {
			const { email } = action.payload
			state.email = email
		},
		setTotalPrice: (state, action) => {
			const { totalPrice } = action.payload
			state.totalPrice = totalPrice
		},
		setIsAuthorized: (state, action) => {
			const { isAuthorized } = action.payload
			state.isAuthorized = isAuthorized
		},
		setImage: (state, action) => {
			const { imageURL } = action.payload
			state.imageURL = imageURL
		},
		setImageFilter: (state, action) => {
			const { filterType } = action.payload
			state.filterType = filterType
		},
		setCartoonifyImage: (state, action) => {
			const { imageURL } = action.payload
			state.cartoonifyImage = imageURL
		},
		setIsLegalFlow: (state, action) => {
			const { isLegalFlow } = action.payload
			state.isLegalFlow = isLegalFlow
		},
		setIsOnlyLegalFlow: (state, action) => {
			const { isOnlyLegalFlow } = action.payload
			state.isOnlyLegalFlow = isOnlyLegalFlow
		},
		setFlowTag: (state, action) => {
			const { flow_tag } = action.payload
			state.flow_tag = flow_tag
		},
		setFlowType: (state, action) => {
			const { flow_type } = action.payload
			state.flow_type = flow_type
		},
		setPayPalToken: (state, action) => {
			const { payPalToken } = action.payload
			state.payPalToken = payPalToken
		},
		setCardToken: (state, action) => {
			const { cardToken } = action.payload
			state.cardToken = cardToken
		},
		setError: (state, action) => {
			const { error } = action.payload
			state.error = error
		},
		setDiscount: (state, action) => {
			const { discount } = action.payload
			state.discount = discount
		},
		setUnsubscribeReason: (state, action) => {
			const { unsubscribeReason } = action.payload
			state.unsubscribeReason = unsubscribeReason
		},
		setBill: (state, action) => {
			if (action.payload.eventName === 'untracked_purchase') {
				ampEventWithEventProperty('untracked_purchase', {
					value: state.isLegalFlow ? state.totalPrice : 0.1,
				})
				state.bill = true
				return
			}
			if (action.payload.alternativePurchase !== -1) {
				if (!state.unsubscribeBill) {
					ampSetUserProperty('payment_type', action.payload.type, state.user_id)
					ampEventWithEventProperty(action.payload.eventName || 'purchase', {
						value: action.payload.alternativePurchase,
					})
					window.fbq('track', 'Purchase', {
						currency: 'USD',
						value: 35.0,
					})
					purchasePropertyFetch(
						{
							amount: action.payload.total,
							type: 'one_time',
						},
						state.user_id,
					)
					if (window?.ttq) {
						window.ttq.track('CompletePayment', {
							value: 35.0,
							currency: 'USD',
						})
					}
					window.twq('track', 'Purchase', {
						value: 35.0,
						currency: 'USD',
						num_items: '1',
					})
				}
				state.unsubscribeBill = true
				return
			}

			if (!state.bill) {
				if (!action.payload.value) {
					ampSetUserProperty('payment_type', action.payload.type, state.user_id)
					ampEventWithEventProperty(action.payload.eventName || 'purchase', {
						value: state.isLegalFlow
							? state.totalPrice
							: action.payload.isABTest
							? 0.99
							: 0.1,
					})
					window.fbq('track', 'Purchase', {
						currency: 'USD',
						value: 35.0,
					})
					purchasePropertyFetch(
						{
							amount: state.initialPrice,
							product_id: state.productId,
							type: state.purchaseType,
						},
						state.user_id,
					)
					if (window?.ttq) {
						window.ttq.track('CompletePayment', {
							value: 35.0,
							currency: 'USD',
						})
					}
					window.twq('track', 'Purchase', {
						currency: 'USD',
						value: 35.0,
					})
				}
			}
			state.bill = true
		},
		setFirstUpsell: state => {
			state.firstUpsell = true
		},
		setSecondUpsell: state => {
			state.secondUpsell = true
		},
		setThirdUpsell: state => {
			state.thirdUpsell = true
		},
		setFourthUpsell: state => {
			state.fourthUpsell = true
		},
		showUnsubscribe: (state, action) => {
			const { isUnsubscribeShown } = action.payload
			state.isUnsubscribeShown = isUnsubscribeShown
		},
		setFbp: (state, action) => {
			state.fbp = action.payload.fbp
		},
		setFbc: (state, action) => {
			state.fbc = action.payload.fbc
		},
		setUserId: (state, action) => {
			state.user_id = action.payload.user_id
			ampSetUserProperty('user_id', action.payload.user_id)
		},
		setConfirmationEmail: state => {
			state.confirmationEmail = true
		},
		setOfferTime: (state, action) => {
			const { offerTime } = action.payload
			state.offerTime = offerTime
		},
		setIsRedirectingToLegal: (state, action) => {
			const { isRedirectingToLegal } = action.payload
			state.isRedirectingToLegal = isRedirectingToLegal
		},
		setCountryCode: (state, action) => {
			const { countryCode } = action.payload
			state.countryCode = countryCode
		},
		setProductId: (state, action) => {
			const { productId } = action.payload
			state.productId = productId
		},
		setPurchaseType: (state, action) => {
			const { purchaseType } = action.payload
			state.purchaseType = purchaseType
		},
		setInitialPrice: (state, action) => {
			const { initialPrice } = action.payload
			state.initialPrice = initialPrice
		},
		extraReducers: builder => {
			builder.addCase(sendConfirmationEmail.fulfilled, (state, action) => {
				state.confirmationEmail = true
			})
		},
	},
})

export const {
	setEmail,
	setIsAuthorized,
	setImage,
	setImageFilter,
	setCartoonifyImage,
	setIsLegalFlow,
	setIsOnlyLegalFlow,
	setFlowTag,
	setFlowType,
	setError,
	setFbc,
	setFbp,
	setCardToken,
	setPayPalToken,
	setUserId,
	setBill,
	setFirstUpsell,
	setSecondUpsell,
	setThirdUpsell,
	setFourthUpsell,
	setDiscount,
	setUnsubscribeReason,
	setConfirmationEmail,
	setTotalPrice,
	showUnsubscribe,
	setOfferTime,
	setIsRedirectingToLegal,
	setCountryCode,
	setProductId,
	setPurchaseType,
	setInitialPrice,
} = userSlice.actions

export const getEmail = state => state.user.email
export const getIsAuthorized = state => state.user.isAuthorized
export const getError = state => state.user.error
export const getImageURL = state => state.user.imageURL
export const getCartoonifyImage = state => state.user.cartoonifyImage
export const getIsLegalFlow = state => state.user.isLegalFlow
export const getIsOnlyLegalFlow = state => state.user.isOnlyLegalFlow
export const getFlowTag = state => state.user.flow_tag
export const getFlowType = state => state.user.flow_type
export const getBill = state => state.user.bill
export const getConfirmationEmail = state => state.user.confirmationEmail
export const getPayPalToken = state => state.user.payPalToken
export const getCardToken = state => state.user.cardToken
export const getFirstUpsell = state => state.user.firstUpsell
export const getSecondUpsell = state => state.user.secondUpsell
export const getThirdUpsell = state => state.user.thirdUpsell
export const getFourthUpsell = state => state.user.fourthUpsell
export const getDiscount = state => state.user.discount
export const getUnsubscribeReason = state => state.user.unsubscribeReason
export const getFbp = state => state.user.fbp
export const getFbc = state => state.user.fbc
export const getUserId = state => state.user.user_id
export const getUnsubscribeBill = state => state.user.unsubscribeBill
export const getIsUnsubscribeShown = state => state.user.isUnsubscribeShown
export const getOfferTime = state => state.user.offerTime
export const getIsRedirectingToLegal = state => state.user.isRedirectingToLegal
export const getCountryCode = state => state.user.countryCode
export const getProductId = state => state.user.productId
export const getPurchaseType = state => state.user.purchaseType
export const getInitialPrice = state => state.user.initialPrice

export default userSlice.reducer
